import { useCallback, useRef, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import { Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import {
  MAX_NAME_LENGTH,
  MAX_URL_LENGTH,
  MIN_SYNC_INTERVAL_SEC,
  emptyInstanceForm,
  instanceToForm,
  toCreateRequest,
  toUpdateRequest,
  validateInstanceForm,
  type InstanceFormField,
  type InstanceFormValues,
} from '@/lib/instanceForm';
import type { Instance } from '@/types/instance';

interface InstanceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create mode. */
  instance: Instance | null;
  onSaved: () => void;
}

/** Shell stays mounted; the form is mounted only while open so every open starts from fresh values. */
export function InstanceFormDialog(props: InstanceFormDialogProps) {
  const submittingRef = useRef(false);
  const { open, onOpenChange } = props;
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return;
        onOpenChange(next);
      }}
    >
      {open ? (
        <InstanceForm {...props} submittingRef={submittingRef} captureOpener={captureOpener} restoreFocus={restoreFocus} />
      ) : null}
    </Dialog>
  );
}

const checkboxClass =
  'mt-0.5 size-4 shrink-0 rounded-sm border border-input accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring';

function InstanceForm({
  instance,
  onOpenChange,
  onSaved,
  submittingRef,
  captureOpener,
  restoreFocus,
}: InstanceFormDialogProps & {
  submittingRef: { current: boolean };
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}) {
  const mode = instance ? 'edit' : 'create';
  const [values, setValues] = useState<InstanceFormValues>(() => (instance ? instanceToForm(instance) : emptyInstanceForm()));
  const [touched, setTouched] = useState<Partial<Record<InstanceFormField, boolean>>>({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSeq, setErrorSeq] = useState(0);

  const nameRef = useRef<HTMLInputElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const tokenRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<HTMLInputElement>(null);
  const focusAlert = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  const errors = validateInstanceForm(values, mode, instance?.sync_interval_sec);
  const shown = (f: InstanceFormField) => (attempted || touched[f] ? errors[f] : undefined);
  const set = <K extends keyof InstanceFormValues>(k: K, v: InstanceFormValues[K]) =>
    setValues((s) => ({ ...s, [k]: v }));
  const blur = (f: InstanceFormField) => setTouched((t) => ({ ...t, [f]: true }));

  const aria = (f: InstanceFormField, helpId?: string) => {
    const ids = [helpId, shown(f) ? `inst-${f}-error` : undefined].filter(Boolean).join(' ');
    return { 'aria-invalid': shown(f) ? true : undefined, 'aria-describedby': ids || undefined } as const;
  };
  const fieldError = (f: InstanceFormField) =>
    shown(f) ? (
      <p id={`inst-${f}-error`} className="text-sm text-destructive">
        {errors[f]}
      </p>
    ) : null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    flushSync(() => setAttempted(true));
    const order: InstanceFormField[] = ['name', 'url', 'portainerToken', 'syncIntervalSec'];
    const firstBad = order.find((f) => errors[f]);
    if (firstBad) {
      ({ name: nameRef, url: urlRef, portainerToken: tokenRef, syncIntervalSec: intervalRef })[firstBad].current?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (instance) await api.instances.update(instance.id, toUpdateRequest(values));
      else await api.instances.create(toCreateRequest(values));
      submittingRef.current = false;
      toast.success(instance ? 'Instance updated' : 'Instance created');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to save instance');
      setErrorSeq((n) => n + 1);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const block = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        nameRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={block}
      onPointerDownOutside={block}
      onInteractOutside={block}
    >
      <DialogHeader>
        <DialogTitle>{instance ? 'Edit instance' : 'Add instance'}</DialogTitle>
        <DialogDescription>
          {instance ? `Update the connection settings for "${instance.name}".` : 'Connect a Portainer instance for the Bridge to sync from.'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="inst-name">Name</Label>
          <Input
            ref={nameRef}
            id="inst-name"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            onBlur={() => blur('name')}
            maxLength={MAX_NAME_LENGTH}
            autoComplete="off"
            placeholder="Production"
            required
            aria-required="true"
            readOnly={submitting}
            {...aria('name')}
          />
          {fieldError('name')}
        </div>

        <div className="space-y-2">
          <Label htmlFor="inst-url">URL</Label>
          <Input
            ref={urlRef}
            id="inst-url"
            type="url"
            autoComplete="off"
            spellCheck={false}
            value={values.url}
            onChange={(e) => set('url', e.target.value)}
            onBlur={() => blur('url')}
            maxLength={MAX_URL_LENGTH + 100}
            placeholder="https://portainer.example.com"
            required
            aria-required="true"
            readOnly={submitting}
            className="font-mono"
            {...aria('url', 'inst-url-help')}
          />
          <p id="inst-url-help" className="text-xs text-muted-foreground">
            Include http:// or https://, for example http://localhost:9000.
          </p>
          {fieldError('url')}
        </div>

        <div className="space-y-2">
          <Label htmlFor="inst-token">Portainer token</Label>
          <Input
            ref={tokenRef}
            id="inst-token"
            type="password"
            autoComplete="new-password"
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            spellCheck={false}
            value={values.portainerToken}
            onChange={(e) => set('portainerToken', e.target.value)}
            onBlur={() => blur('portainerToken')}
            required={mode === 'create'}
            aria-required={mode === 'create' ? 'true' : undefined}
            readOnly={submitting}
            {...aria('portainerToken', 'inst-token-help')}
          />
          <p id="inst-token-help" className="text-xs text-muted-foreground">
            {mode === 'create' ? 'Paste a Portainer API token.' : 'Leave empty to keep the current token.'}
          </p>
          {fieldError('portainerToken')}
        </div>

        <div className="space-y-2">
          <Label htmlFor="inst-interval">Sync interval (seconds)</Label>
          <Input
            ref={intervalRef}
            id="inst-interval"
            inputMode="numeric"
            autoComplete="off"
            value={values.syncIntervalSec}
            onChange={(e) => set('syncIntervalSec', e.target.value)}
            onBlur={() => blur('syncIntervalSec')}
            required
            aria-required="true"
            readOnly={submitting}
            className="tabular-nums"
            {...aria('syncIntervalSec', 'inst-interval-help')}
          />
          <p id="inst-interval-help" className="text-xs text-muted-foreground">
            {mode === 'edit' && instance
              ? `Currently ${instance.sync_interval_sec} seconds. New values must be at least ${MIN_SYNC_INTERVAL_SEC}.`
              : `Seconds between syncs (at least ${MIN_SYNC_INTERVAL_SEC}).`}
          </p>
          {fieldError('syncIntervalSec')}
        </div>

        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <input
              id="inst-skip-tls"
              type="checkbox"
              className={checkboxClass}
              checked={values.skipTlsVerify}
              onChange={(e) => { if (!submitting) set('skipTlsVerify', e.target.checked); }}
              aria-describedby={values.skipTlsVerify ? 'inst-skip-tls-warning' : undefined}
              aria-disabled={submitting}
              onClickCapture={(e) => {
                if (submitting) e.preventDefault();
              }}
            />
            <Label htmlFor="inst-skip-tls">Skip TLS verification</Label>
          </div>
          {values.skipTlsVerify ? (
            <p id="inst-skip-tls-warning" className="flex items-start gap-1.5 text-sm text-status-warning">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>Only use this for self-signed certificates on networks you trust.</span>
            </p>
          ) : null}
        </div>

        {mode === 'edit' ? (
          <div className="space-y-1">
            <div className="flex items-start gap-2">
              <input
                id="inst-active"
                type="checkbox"
                className={checkboxClass}
                checked={values.isActive}
                onChange={(e) => { if (!submitting) set('isActive', e.target.checked); }}
                aria-describedby="inst-active-help"
                aria-disabled={submitting}
                onClickCapture={(e) => {
                  if (submitting) e.preventDefault();
                }}
              />
              <Label htmlFor="inst-active">Active</Label>
            </div>
            <p id="inst-active-help" className="pl-6 text-xs text-muted-foreground">
              Inactive instances aren't synced.
            </p>
          </div>
        ) : null}

        {error ? (
          <p key={errorSeq} ref={focusAlert} tabIndex={-1} role="alert" className="text-sm text-destructive outline-none">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            aria-disabled={submitting}
            onClick={() => {
              if (!submitting) onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            aria-disabled={submitting}
            aria-busy={submitting}
            className={submitting ? 'pointer-events-none opacity-70' : undefined}
          >
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Saving…' : instance ? 'Save changes' : 'Add instance'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
